import React from 'react';

import classNames from 'classnames';
import { Field, FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { validationErrors } from './costAllocationApi';
import DialogModal from './DialogModal';
import { parseNumber } from './formatting';

import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

import PlusIcon from 'Assets/icons/plus.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const emptyColumn = () => ({ name: '', note: '', default_pct: '' });

const isBlankRow = (column) => !String(column?.name ?? '').trim() && !String(column?.note ?? '').trim() && !String(column?.default_pct ?? '').trim();

const schema = (existingTitles) =>
    Yup.object({
        title: Yup.string()
            .trim()
            .required($t('Enter a report title.'))
            .max(191)
            .test('unique', $t('A report with this title already exists.'), (value) => !existingTitles.includes((value || '').trim().toLowerCase())),
        mode: Yup.string().oneOf(['define', 'copy']),
        copy_from_report_id: Yup.string().when('mode', (mode, field) =>
            mode === 'copy' ? field.required($t('Choose a report to copy columns from.')) : field,
        ),
        columns: Yup.array().when('mode', (mode, field) =>
            mode === 'define'
                ? field.of(
                      Yup.object({
                          name: Yup.string()
                              .max(191)
                              .test('required', $t('Enter a column name.'), function (value) {
                                  return isBlankRow(this.parent) || !!(value || '').trim();
                              }),
                          default_pct: Yup.string().test('pct', $t('Enter a percentage between 0 and 999.'), (value) => {
                              const pct = parseNumber(value);

                              return pct === null || (!Number.isNaN(pct) && pct >= 0 && pct <= 999);
                          }),
                      }),
                  )
                : field,
        ),
    });

/** Flags every column whose name another column already uses, ignoring case. */
const validateDistinctNames = (values) => {
    if (values.mode !== 'define') {
        return {};
    }

    const names = values.columns.map((column) => column.name.trim().toLowerCase());
    const columns = names.map((name) =>
        name && names.filter((other) => other === name).length > 1 ? { name: $t('Each column needs a different name.') } : undefined,
    );

    return columns.some(Boolean) ? { columns } : {};
};

/** New report: a title, then columns defined now (name, note, default %) or copied from another report. */
export default function CreateReportModal({ isOpen, onClose, reports, onCreate }) {
    const existingTitles = reports.map((report) => report.title.trim().toLowerCase());

    const submit = async (values, { setErrors, setSubmitting }) => {
        const kept = values.columns.map((column, index) => ({ column, index })).filter(({ column }) => !isBlankRow(column));
        const payload =
            values.mode === 'copy'
                ? { title: values.title.trim(), mode: 'copy', copy_from_report_id: Number(values.copy_from_report_id) }
                : {
                      title: values.title.trim(),
                      mode: 'define',
                      columns: kept.map(({ column }) => ({
                          name: column.name.trim(),
                          note: column.note.trim() || null,
                          default_pct: parseNumber(column.default_pct),
                      })),
                  };

        try {
            await onCreate(payload);
        } catch (error) {
            const errors = validationErrors(error);

            if (!errors) {
                setSubmitting(false);
                return;
            }

            const formErrors = { columns: [] };
            Object.entries(errors).forEach(([key, message]) => {
                const match = key.match(/^columns\.(\d+)\.(\w+)$/);

                if (match) {
                    const original = kept[Number(match[1])]?.index ?? 0;
                    formErrors.columns[original] = { ...(formErrors.columns[original] || {}), [match[2]]: message };
                } else {
                    formErrors[key] = message;
                }
            });
            setErrors(formErrors);
            setSubmitting(false);
        }
    };

    return (
        <DialogModal
            id="cost-allocation-create-report"
            isOpen={isOpen}
            onClose={onClose}
            title={$t('New cost allocation report')}
            subtitle={$t('A report splits the budget across a set of columns. Set the columns now or add them later.')}
        >
            <Formik
                initialValues={{
                    title: '',
                    mode: 'define',
                    copy_from_report_id: reports[0] ? String(reports[0].id) : '',
                    columns: [emptyColumn(), emptyColumn()],
                }}
                validationSchema={schema(existingTitles)}
                validate={validateDistinctNames}
                validateOnChange={false}
                onSubmit={submit}
            >
                {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form className="modal__form-container">
                        <div className="tw-flex tw-flex-col tw-gap-y-1.5">
                            <label htmlFor="ca-report-title" className="text-input__label !tw-normal-case">
                                {$t('Report title')}
                            </label>
                            <Field
                                id="ca-report-title"
                                name="title"
                                autoFocus
                                maxLength={191}
                                placeholder={$t('e.g. Venue cost share')}
                                className={classNames('text-input', { 'tw-border tw-border-red-500': touched.title && errors.title })}
                            />
                            {touched.title && errors.title && <div className="tw-text-sm tw-text-red-500">{errors.title}</div>}
                        </div>

                        <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-y-2">
                            <span className="text-input__label !tw-normal-case">{$t('Columns')}</span>
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-text-sm">
                                <label className="tw-flex tw-items-center tw-gap-x-1.5">
                                    <Field type="radio" name="mode" value="define" />
                                    {$t('Define columns now')}
                                </label>
                                {reports.length > 0 && (
                                    <label className="tw-flex tw-items-center tw-gap-x-1.5">
                                        <Field type="radio" name="mode" value="copy" />
                                        {$t('Copy columns from')}
                                        <Field
                                            as="select"
                                            name="copy_from_report_id"
                                            className="text-input !tw-w-auto !tw-py-1"
                                            onFocus={() => setFieldValue('mode', 'copy')}
                                        >
                                            {reports.map((report) => (
                                                <option key={report.id} value={report.id}>
                                                    {report.title}
                                                </option>
                                            ))}
                                        </Field>
                                    </label>
                                )}
                            </div>
                            {errors.copy_from_report_id && <div className="tw-text-sm tw-text-red-500">{errors.copy_from_report_id}</div>}

                            {values.mode === 'define' && (
                                <FieldArray name="columns">
                                    {({ push, remove }) => (
                                        <div className="tw-flex tw-flex-col tw-gap-y-2">
                                            <div className="tw-grid tw-grid-cols-[1.1fr_1.6fr_90px_28px] tw-gap-x-2 tw-text-2xs tw-text-charcoal-50">
                                                <span>{$t('Name')}</span>
                                                <span>{$t('Note (optional)')}</span>
                                                <span>{$t('Default %')}</span>
                                                <span />
                                            </div>
                                            {values.columns.map((column, index) => {
                                                const rowErrors = errors.columns?.[index] || {};

                                                return (
                                                    <div key={index} className="tw-flex tw-flex-col tw-gap-y-1">
                                                        <div className="tw-grid tw-grid-cols-[1.1fr_1.6fr_90px_28px] tw-items-center tw-gap-x-2">
                                                            <Field
                                                                name={`columns.${index}.name`}
                                                                maxLength={191}
                                                                aria-label={$t('Column name')}
                                                                placeholder={$t('e.g. City')}
                                                                className={classNames('text-input', { 'tw-border tw-border-red-500': rowErrors.name })}
                                                            />
                                                            <Field
                                                                name={`columns.${index}.note`}
                                                                aria-label={$t('Note (optional)')}
                                                                placeholder={$t('Basis or source')}
                                                                className="text-input"
                                                            />
                                                            <Field
                                                                name={`columns.${index}.default_pct`}
                                                                inputMode="decimal"
                                                                aria-label={$t('Default %')}
                                                                placeholder={$t('blank')}
                                                                className={classNames('text-input', { 'tw-border tw-border-red-500': rowErrors.default_pct })}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => remove(index)}
                                                                title={$t('Remove')}
                                                                aria-label={$t('Remove')}
                                                                className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md tw-text-charcoal-50 hover:tw-bg-charcoal-15 hover:tw-text-charcoal"
                                                            >
                                                                <TrashIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                                                            </button>
                                                        </div>
                                                        {(rowErrors.name || rowErrors.default_pct) && (
                                                            <div className="tw-text-sm tw-text-red-500">{rowErrors.name || rowErrors.default_pct}</div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            <div>
                                                <TextButton className="!tw-normal-case" variant="primary" onClick={() => push(emptyColumn())}>
                                                    <PlusIcon className="tw-h-3 tw-w-3" aria-hidden="true" />
                                                    {$t('Add another column')}
                                                </TextButton>
                                            </div>
                                        </div>
                                    )}
                                </FieldArray>
                            )}
                        </div>

                        <div className="modal__footer tw-justify-end tw-gap-x-1.5">
                            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                                {$t('Cancel')}
                            </Button>
                            <Button variant="primary" type="submit" disabled={isSubmitting} loading={isSubmitting}>
                                {isSubmitting ? $t('Creating…') : $t('Create report')}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </DialogModal>
    );
}
